/**
 * Sales Order Type Definitions.
 */

export interface SOLineCharge {
  id: string;
  line_id: string;
  charge_type: string;
  description: string;
  amount: number;
  /** Whether this charge joins the order's taxable base. */
  is_taxable: boolean;
  /** Set when a scheme created the charge; null when entered by hand. */
  tax_scheme_id: string | null;
  created_at: string;
}

export interface SOLine {
  id: string;
  sales_order_id: string;
  /** Null on a manual line — something billed that is not tracked inventory. */
  asset_id: string | null;
  description: string | null;
  category: string | null;
  /** Always 1 on an asset-backed line; free on a manual line. */
  quantity: number;
  unit_price: number;
  discount_type: "percent" | "amount" | null;
  discount_value: number;
  discount_amount: number;
  /** quantity * unit_price - discount_amount. */
  total_price: number;
  /** Legacy alias for total_price, net of any line discount. */
  sale_price: number;
  line_fees: number;
  line_total: number;
  /** Manual lines only: cost per unit, for margin reporting. */
  unit_cost: number | null;
  cogs: number;
  /**
   * Where cogs came from: "asset" (snapshotted), "manual" (from unit_cost), or
   * "none" (no cost available, so margin on this line is not meaningful —
   * do not render it as 100%).
   */
  cogs_source: "asset" | "manual" | "none";
  gross_profit: number;
  created_at: string;
  charges: SOLineCharge[];
}

export interface CommissionRecord {
  id: string;
  organization_id: string;
  sales_order_id: string;
  salesperson_id: string;
  basis: string;
  rate: number;
  amount: number;
  status: string;
  paid_at: string | null;
  created_at: string;
}

export interface OrderTaxLine {
  id: string;
  /**
   * Which total this row belongs in. A "fee" row is a delivery or documentation
   * charge, not tax — it must never be summed into a tax figure or shown under a
   * Tax heading. Older rows may arrive without it; treat a missing value as
   * "tax", which is what the backend defaults it to.
   */
  charge_class: "tax" | "fee";
  /** Fees only: whether this charge is itself part of the taxable base. */
  is_taxable: boolean;
  tax_scheme_id: string | null;
  scheme_name: string;
  scheme_type: string;
  rate: number;
  flat_amount: number;
  taxable_amount: number;
  calculated_tax: number;
  applied_tax: number;
  is_overridden: boolean;
  /**
   * How this line was determined, so the UI can label it precisely:
   * null/"auto" (engine), "forced_scheme", "manual_percent", "manual_amount",
   * "exempt", or "manual_charge".
   *
   * "manual_charge" marks an ad hoc order charge. Those rows also carry
   * is_overridden = true, but as plumbing that stops a recalculation deleting
   * them — not because a user overrode the tax. Reading it as a tax directive
   * labels the order's tax "Overridden" when someone merely added a delivery fee.
   */
  override_mode: string | null;
  override_reason: string | null;
  certificate_id: string | null;
  certificate_verified: boolean;
  note: string | null;
}

export interface SalesOrder {
  id: string;
  organization_id: string;
  so_number: string;
  customer_id: string;
  customer_name: string | null;
  customer_email: string | null;
  salesperson_id: string | null;
  salesperson_name: string | null;
  location_id: string | null;
  status: string;
  order_date: string | null;
  delivery_date: string | null;
  currency: string;
  subtotal: number;
  discount_type: "percent" | "amount" | null;
  discount_value: number;
  discount_amount: number;
  tax: number;
  /** Deprecated: read-only, retained for historical orders. Use charges. */
  shipping: number;
  trade_in_credit: number;
  /** Every charge on the order: line charges plus order-level fee lines. */
  fees_total: number;
  total: number;
  commission_rate: number | null;
  commission_amount: number;
  payment_method: string | null;
  payment_reference: string | null;
  paid_at: string | null;
  notes: string | null;
  created_by: string | null;
  created_at: string;
  updated_at: string;
  line_items: SOLine[];
  commissions: CommissionRecord[];
  tax_lines: OrderTaxLine[];
}

export interface SalesOrderCreate {
  customer_id: string;
  salesperson_id?: string;
  order_date?: string;
  delivery_date?: string;
  /**
   * Tax and shipping are deliberately absent. Tax is engine-owned and its manual
   * path is the tax-override route, which records an audit trail; delivery money
   * is an itemised charge.
   */
  discount_type?: "percent" | "amount";
  discount_value?: number;
  trade_in_credit?: number;
  commission_rate?: number;
  notes?: string;
}

/**
 * Payload for updating a sales order. Only what is sent is changed.
 *
 * Tax and shipping are absent, matching the server: tax is engine-owned and set
 * through the tax directive, which records an audit trail, and delivery money is
 * an itemised charge.
 */
export interface SalesOrderUpdate {
  customer_id?: string;
  salesperson_id?: string | null;
  order_date?: string | null;
  delivery_date?: string | null;
  discount_type?: "percent" | "amount" | null;
  discount_value?: number;
  trade_in_credit?: number;
  commission_rate?: number | null;
  notes?: string | null;
}

/**
 * Payload for adding a line to a sales order.
 *
 * Two shapes. An asset-backed line names an `asset_id` and is always one unit —
 * the server rejects any other quantity, because an asset is identified
 * individually. A manual line has no asset and needs a `description`, which is
 * what the customer reads on the invoice.
 *
 * `sale_price` is a legacy alias for `unit_price`, kept so existing callers work.
 */
export interface SOLineCreate {
  asset_id?: string;
  description?: string;
  category?: string;
  quantity?: number;
  unit_price?: number;
  /** Legacy alias for unit_price. */
  sale_price?: number;
  discount_type?: "percent" | "amount";
  discount_value?: number;
  /** Manual lines only: cost per unit, for margin reporting. */
  unit_cost?: number;
}

/** Payload for editing a line. Only what is sent is changed. */
export interface SOLineUpdate {
  description?: string;
  quantity?: number;
  unit_price?: number;
  discount_type?: "percent" | "amount" | null;
  discount_value?: number;
  unit_cost?: number;
}

/**
 * Payload for adding a charge to a sales order line.
 *
 * `is_taxable` has no default here because it has none on the server: whether a
 * delivery charge is itself taxed varies by state and by whether it is separately
 * stated, and a charge that quietly defaults to non-taxable is the seller's
 * liability at audit.
 */
export interface SOLineChargeCreate {
  charge_type: string;
  description: string;
  amount: number;
  is_taxable: boolean;
}

/** Payload for an ad hoc charge on the order as a whole. */
export interface SOOrderChargeCreate {
  description: string;
  amount: number;
  is_taxable: boolean;
  reason?: string;
}

/** What an "Apply Schemes" run landed, so the UI can say rather than guess. */
export interface SOSchemeApplySummary {
  line_charges_created: number;
  line_fees_total: number;
  order_tax_total: number;
  order_fees_total: number;
}

export interface ApplySOSchemesResponse {
  sales_order: SalesOrder;
  summary: SOSchemeApplySummary;
}

export interface CustomerContact {
  id: string;
  customer_id: string;
  name: string;
  title: string | null;
  email: string | null;
  phone: string | null;
  mobile: string | null;
  is_primary: boolean;
  notes: string | null;
  created_at: string;
}

export interface Customer {
  id: string;
  organization_id: string;
  name: string;
  company_name: string | null;
  email: string | null;
  phone: string | null;
  address: Record<string, string> | null;
  website: string | null;
  tax_id: string | null;
  payment_terms: string | null;
  tax_tags: string[] | null;
  tax_exempt: boolean;
  default_tax_state: string | null;
  notes: string | null;
  is_active: boolean;
  created_at: string;
  updated_at: string;
  contacts: CustomerContact[];
}

export interface CustomerCreate {
  name: string;
  company_name?: string;
  email?: string;
  phone?: string;
  website?: string;
  tax_id?: string;
  payment_terms?: string;
  tax_tags?: string[];
  tax_exempt?: boolean;
  default_tax_state?: string;
  notes?: string;
}

export interface TaxCertificate {
  id: string;
  organization_id: string;
  customer_id: string | null;
  certificate_type: string;
  certificate_number: string | null;
  issuing_state: string | null;
  issued_date: string | null;
  expiration_date: string | null;
  is_revoked: boolean;
  is_current: boolean;
  document_url: string | null;
  notes: string | null;
  uploaded_by: string | null;
  created_at: string;
  updated_at: string;
}

export interface TaxScheme {
  id: string;
  organization_id: string;
  name: string;
  description: string | null;
  scheme_type: string;
  rate: number;
  flat_amount: number;
  applies_to: string;
  /** 'line' = materialised as a per-line charge; 'order' = an order-level row. */
  apply_level: "line" | "order";
  /**
   * What kind of money the scheme produces. Independent of apply_level, which
   * only says where it is computed: a percentage delivery surcharge and a flat
   * state excise are both normal.
   */
  charge_class: "tax" | "fee";
  /** Fees only: whether the fee joins the taxable base. Required when creating one. */
  is_taxable: boolean;
  is_default: boolean;
  priority: number;
  condition_customer_tags: string[] | null;
  condition_asset_categories: string[] | null;
  condition_interstate: boolean | null;
  condition_same_state: boolean | null;
  condition_states: string[] | null;
  requires_certificate: boolean;
  certificate_type: string | null;
  certificate_must_be_on_file: boolean;
  certificate_per_order: boolean;
  certificate_renewal_months: number;
  conditions: Record<string, unknown>;
  is_active: boolean;
  sort_order: number;
  created_at: string;
  updated_at: string;
}

export interface TaxCalculationResult {
  total_tax: number;
  applied_taxes: Array<{
    scheme_id: string | null;
    scheme_name: string;
    scheme_type: string;
    rate: number;
    flat_amount: number;
    taxable_amount: number;
    calculated_amount: number;
    certificate_id: string | null;
    certificate_verified: boolean;
    note: string | null;
    warnings: string[];
  }>;
  warnings: string[];
}

export interface DashboardKPIs {
  total_inventory: number;
  total_inventory_value: number;
  open_purchase_orders: number;
  active_work_orders: number;
  monthly_sales_count: number;
  monthly_sales_revenue: number;
  avg_days_in_inventory: number;
  action_alert_count: number;
}

export const SO_STATUSES = [
  "Draft",
  "Confirmed",
  "Invoiced",
  "Paid",
  "Cancelled",
] as const;
